import { useState, useEffect, useCallback } from 'react';
import { useReaderAuth } from '../context/ReaderAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook to check course access permissions.
 * Returns overall access status, per-module purchased array, and loading state.
 */
export const useCourseAccess = () => {
  const { user, token, isAuthenticated } = useReaderAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [accessType, setAccessType] = useState(null); // 'whitelisted' | 'purchased' | null
  const [purchasedModules, setPurchasedModules] = useState([]); // ['module-2', 'module-3', ...]
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAccess = useCallback(async () => {
    if (!isAuthenticated() || !token) {
      setHasAccess(false);
      setAccessType(null);
      setPurchasedModules([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/reader/check-course-access`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setHasAccess(false);
          setAccessType(null);
          setPurchasedModules([]);
        } else {
          throw new Error('Failed to check course access');
        }
      } else {
        const data = await response.json();
        setHasAccess(data.has_access);
        setAccessType(data.access_type);
        setPurchasedModules(data.purchased_modules || []);
      }
    } catch (err) {
      console.error('Error checking course access:', err);
      setError(err.message);
      setHasAccess(false);
      setPurchasedModules([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const isWhitelisted = accessType === 'whitelisted';

  /** Returns true if this specific module number is accessible */
  const hasModuleAccess = (moduleNumber) => {
    if (moduleNumber === 1) return true; // always free
    if (isWhitelisted || hasAccess) return true;
    return purchasedModules.includes(`module-${moduleNumber}`);
  };

  return {
    hasAccess,
    accessType,
    isWhitelisted,
    purchasedModules,
    hasModuleAccess,
    isLoading,
    error,
    isAuthenticated: isAuthenticated(),
    user,
    refetchAccess: checkAccess
  };
};

export default useCourseAccess;
